window.Foxtheme = window.Foxtheme || {};

const PUB_SUB_EVENTS = {
  cartError: 'cart-error'
};

function generateDomFromString(value, tagName = "div") {
  const d = document.createElement(tagName);
  d.innerHTML = value;
  return d;
}

function getScrollbarWidth() {
  const outer = document.createElement("div");
  outer.style.visibility = "hidden";
  outer.style.overflow = "scroll";
  outer.style.msOverflowStyle = "scrollbar";
  document.body.appendChild(outer);
  const inner = document.createElement("div");
  outer.appendChild(inner);
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
  outer.parentNode.removeChild(outer);
  return scrollbarWidth;
}

const sectionCache = new Map();
function fetchSection(sectionId, options = {}) {
  const { url: _url, fromCache = false, params = {} } = options;
  return new Promise((resolve, reject) => {
    const url = new URL(_url || window.location.href);
    url.searchParams.set("section_id", sectionId);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

    if (fromCache) {
      const cached = sectionCache.get(url);
      if (cached) return resolve(cached);
    }

    fetch(url, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.ok) return res.text();
        reject(`Failed to load section: ${sectionId}`);
      })
      .then((html) => {
        const div = generateDomFromString(html);
        sectionCache.set(url, div);
        resolve(div);
      })
      .catch(reject);
  });
}

function loadAssets(files = [], id, callback = () => {}, options = {}) {
  const unique = id ? id : Math.random().toString(36).slice(2);
  if (!window.FoxTheme.loadjs.isDefined(id))
    window.FoxTheme.loadjs(files, unique);
  window.FoxTheme.loadjs.ready(unique, callback);
}

function queryDomNodes(selectors = {}, context = document) {
  const domNodes = Object.entries(selectors).reduce((acc, [name, selector]) => {
    const findOne = typeof selector === "string";
    const queryMethod = findOne ? "querySelector" : "querySelectorAll";
    const sl = findOne ? selector : selector[0];

    acc[name] = context && context[queryMethod](sl);
    if (!findOne && acc[name]) {
      acc[name] = [...acc[name]];
    }
    return acc;
  }, {});
  return domNodes;
}

function addEventDelegate({
  context = document.documentElement,
  event = "click",
  selector,
  handler,
  capture = false,
}) {
  const listener = function (e) {
    // loop parent nodes from the target to the delegation node
    for (
      let target = e.target;
      target && target !== this;
      target = target.parentNode
    ) {
      if (target.matches(selector)) {
        handler.call(target, e, target);
        break;
      }
    }
  };
  context.addEventListener(event, listener, capture);
  return () => {
    context.removeEventListener(event, listener, capture);
  };
}

function isStorageSupported(type) {
  // Return false if we are in an iframe without access to sessionStorage
  if (window.self !== window.top) {
    return false;
  }

  const testKey = "minimog:check";
  let storage;
  if (type === "session") {
    storage = window.sessionStorage;
  }
  if (type === "local") {
    storage = window.localStorage;
  }

  try {
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    // Do nothing, this may happen in Safari in incognito mode
    return false;
  }
}

const moneyFormat = "${{amount}}";
function formatMoney(cents, format) {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

function intersecting(options) {
  var threshold = options.threshold ? options.threshold : 0;
  var observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof options.callback === "function") {
            options.callback();
            observer.unobserve(entry.target);
          }
        }
      });
    },
    { rootMargin: "0px 0px " + threshold + "px 0px" }
  );
  observer.observe(options.element);
}

class AnimateComponent extends HTMLElement {
  constructor() {
    super();

    intersecting({
      element: this,
      callback: this.init.bind(this),
      threshold: 0,
    });
  }

  init() {
    this.setAttribute("is-visible", true);
  }
}
customElements.define("animate-component", AnimateComponent);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", "false");

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  console.log('trap the focus')
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia(container = document) {
  container.querySelectorAll(".js-youtube").forEach((video) => {
    if (!video.classList.contains("video-playsinline")) {
      video.contentWindow.postMessage(
        '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
        "*"
      );
    }
  });
  container.querySelectorAll(".js-vimeo").forEach((video) => {
    if (!video.classList.contains("video-playsinline")) {
      video.contentWindow.postMessage('{"method":"pause"}', "*");
    }
  });
  container.querySelectorAll("video").forEach((video) => {
    if (!video.classList.contains("video-playsinline")) {
      video.pause();
    }
  });
  container.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function cookiesEnabled() {
  let cookieEnabled = navigator.cookieEnabled;

  if (!cookieEnabled) {
    document.cookie = "testcookie";
    cookieEnabled = document.cookie.indexOf("testcookie") !== -1;
  }
  return cookieEnabled;
}

function setCookie(cname, cvalue, exdays) {
  var d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  var expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

function getCookie(cname) {
  var name = cname + "=";
  var decodedCookie = decodeURIComponent(document.cookie);
  var ca = decodedCookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

function getSrcset(src, sizes = ["360", "533", "720", "940", "1066"]) {
  sizes = sizes.map((size) => {
    return `${getSizedImageUrl(src, size + "x")} ${size}w`;
  });
  return sizes.join(", ");
}

function removeProtocol(path) {
  return path.replace(/http(s)?:/, "");
}

function getSizedImageUrl(src, size) {
  if (size === null) {
    return src;
  }

  if (size === "master") {
    return removeProtocol(src);
  }

  const match = src.match(
    /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i
  );

  if (match) {
    const prefix = src.split(match[0]);
    const suffix = match[0];

    return removeProtocol(`${prefix[0]}_${size}${suffix}`);
  } else {
    return null;
  }
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value === option.value || value === option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length === 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.initialLoad = true;
    this.resizeState = 'desktop';
  }
  isSliderEnabledForCurrentViewport() {
    const width = window.innerWidth;
    const hasBreakpointScopedSliderClass =
      this.classList.contains("xxl:flickity-enable") ||
      this.classList.contains("xl:flickity-enable") ||
      this.classList.contains("lg:flickity-enable") ||
      this.classList.contains("md:flickity-enable") ||
      this.classList.contains("sm:flickity-enable");

    if (!hasBreakpointScopedSliderClass) return true;

    if (this.classList.contains("xxl:flickity-enable")) return width >= 1536;
    if (this.classList.contains("xl:flickity-enable")) return width >= 1280;
    if (this.classList.contains("lg:flickity-enable")) return width >= 1024;
    if (this.classList.contains("md:flickity-enable")) return width >= 768;
    if (this.classList.contains("sm:flickity-enable")) return width >= 640;
    if (this.classList.contains("flickity-enable")) return true;

    return false;
  }
  connectedCallback() {
    const { sliderColumns, enableSlider } = this.dataset;
    if (enableSlider !== "true") return;
    if (!this.isSliderEnabledForCurrentViewport()) return;
    if (this.classList.contains("f-recently-viewed-flickity")) {
      this.initSlider();
    } else if (this.childElementCount > parseInt(sliderColumns)) {
      this.initSlider();
    } else if (this.childElementCount <= parseInt(sliderColumns)) {
      // Fix slider hidden.
      this.classList.remove('md:flickity-enable')
    }
  }
  initSlider() {
    this.slider = new window.FoxTheme.Slider(this);
    this.disablePointerMove = this.dataset.disablePointerMove || true;
    this.paginateCounterWrapper = this.querySelector(".flickity-page-counter");
    if (
      this.slider &&
      this.slider.instance &&
      this.dataset.disablePointerMove !== "false"
    ) {
            this.classList.add('f-flickity-loaded')
      this.slider.instance.on("pointerMove", () => {
        this.classList.add("is-pointer-move");
      });
      this.slider.instance.on("pointerUp", () => {
        this.classList.remove("is-pointer-move");
      });
    }

    // Handle page counter
    if (this.slider && this.slider.instance && this.paginateCounterWrapper) {
      if (this.classList.contains("md:flickity-enable")) {
        this.paginateCounterWrapper.classList.remove("md:hidden");
        this.paginateCounterWrapper.classList.add("md:inline-flex", "hidden");
      } else {
        this.paginateCounterWrapper.classList.remove("hidden");
        this.paginateCounterWrapper.classList.add("inline-flex");
      }

      this.slider.instance.on("change", this.handlePaginateCounter.bind(this));
    }

    if (this.slider && this.slider.instance) {
      this.initialized = true;
      // prevent hidden slides from being in tab order (ADA required)
      this.updateHiddenSlideTabbingBound = this.updateHiddenSlideTabbing.bind(this);
      this.updateHiddenSlideTabbingBound();
      
      // rerun hiding slides on slide changes (ADA required)
      this.slider.instance.on('ready', this.updateHiddenSlideTabbingBound);
      this.slider.instance.on('select', this.updateHiddenSlideTabbingBound);
      this.slider.instance.on('change', this.updateHiddenSlideTabbingBound);
      this.slider.instance.on('settle', this.updateHiddenSlideTabbingBound);
      
      window.onresize = () => {
        const flickityViewPort = this.querySelector('.flickity-viewport')
        if (flickityViewPort) { 
          flickityViewPort.style.height = null;
        }
        
        const slider = document.querySelector('.f-product__media-list');
        let w = window.innerWidth;

        if ( slider != null ) {
          if ( w < 768 && this.resizeState == 'desktop' ) {
            console.log( 'MOVE TO MOBILE STATE' );
            this.resizeState = 'mobile';

            // clean up any empty viewports
            const sliderVps = slider.querySelectorAll('.flickity-viewport .flickity-slider');
            for ( let i = 0; i < sliderVps.length; i++ ) {
              if ( sliderVps[i].childNodes.length < 0 ) {
                sliderVps[i].parentElement.remove();
              } 
            }

            // init slider
            slider.classList.remove('flickity-destroyed');
            slider.initSlider();
            const variantPicker = document.querySelector('variant-picker');
            if ( variantPicker ) {
              // variantPicker.initHijack();
            }
          } else if ( w >= 768 && this.resizeState == 'mobile' )  {
            // console.log( 'MOVE TO DESKTOP STATE' );
            this.resizeState = 'desktop';
            slider.destroy();
          } else if ( w < 768 && this.resizeState == 'mobile' ) {
            // console.log( 'CURRENTLY IN MOBILE VIEW' );
            if ( w == 640 && this.resizeState != 'odd' ) {
              this.resizeState = 'odd';
              // console.log( 'ODD BREAKPOINT HIT!' );
            }
          } else if ( ( w > 640 || w < 640 ) && this.resizeState == 'odd' ) {
            // console.log( 'MOVED OUT OF ODD STATE! RETURNING TO MOBILE' );
            this.resizeState = 'mobile';
           
            // console.log( 'CLEANUP ROUTINE' );
            let tSlider = document.querySelector('.f-product__media-list');
            tSlider.destroy();
            tSlider.classList.remove('flickity-destroyed');
            tSlider.initSlider();
            const variantPicker = document.querySelector('variant-picker');
            if ( variantPicker ) {
              // variantPicker.initHijack()
            }

            const sliderVps = tSlider.querySelectorAll('.flickity-viewport');
            for ( let i = 0; i < sliderVps.length; i++ ) {
              let t = sliderVps[i].querySelector('.flickity-slider');
              if ( t ) {
                console.log( t );
                if ( t.childNodes.length <= 0 ) {
                  sliderVps[i].remove();
                  // console.log( 'REMOVING EXTRA NODE')
                }
              }
            }
          }
        }

        this.update();
      }
      
      if ( this.initialLoad == true ) {
        let w = window.innerWidth;
        this.initialLoad = false;

        if ( w < 768 ) {
          this.resizeState = 'mobile';
          const slider = document.querySelector('.f-product__media-list');
          if ( slider ) {
            slider.initSlider();
          }
        }
      }

      // Fix media slider not init on Safari
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    }

    if (this.classList.contains("f-recently-viewed-flickity") && this.childElementCount <= parseInt(this.dataset.sliderColumns)) {
      this.classList.remove("md:flickity-enable");
    }
  }
  update() {
    if (this.slider && this.slider.instance) {
      this.slider.instance.resize();
    }
  }
  destroy() {
    if (typeof this.slider.instance === "object") {
      this.slider.instance.destroy();
      this.classList.add("flickity-destroyed");
      this.slider.instance.off("pointerMove");
      this.slider.instance.off("pointerUp");
    }
  }
  select(index, isWrapped = false, isInstant = false) {
    this.slider && this.slider.instance.select(index, isWrapped, isInstant);
  }
  toggleFade(isFade = false) {
    const method = isFade ? "add" : "remove";
    this.slider.instance.options.fade = isFade;
    this.classList[method]("is-fade");
    this.update();
  }
  handlePaginateCounter(index) {
    const sliderCounterCurrent = this.querySelector(
      ".flickity-counter--current"
    );
    if (sliderCounterCurrent) {
      sliderCounterCurrent.textContent = index + 1;
    }
  }
  updateHiddenSlideTabbing() {
    const flickityInstance = this.slider?.instance;
    const flickityCells = flickityInstance?.cells
      ? flickityInstance.cells.map(cell => cell.element)
      : [];

    let slides;

    if (flickityCells.length > 0) {
      slides = flickityCells;
    } else {
      slides = Array.from(this.children).filter(item =>
        item.classList.contains('f-column')
      );
    }

    if (slides.length === 0) {
      return;
    }

    slides.forEach((slide) => {
      const isClone = slide.classList.contains('is-cloned');
      const isVisible = slide.classList.contains('is-selected') && !isClone;

      if (isVisible) {
        slide.removeAttribute('inert');
        // Optional: if Flickity/theme already manages aria-hidden, you can remove this line.
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('inert', '');
        // Optional but recommended; does not stop tabbing by itself.
        slide.setAttribute('aria-hidden', 'true');
      }
    });
  }
}
customElements.define("flickity-component", SliderComponent);
class PressNavComponent extends SliderComponent {
  constructor() {
    super();

    document.addEventListener("matchMobile", () =>
      this.slider.instance.resize()
    );
    document.addEventListener("unmatchMobile", () =>
      this.slider.instance.resize()
    );
  }
}
customElements.define("press-nav-component", PressNavComponent);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    const autoplay = poster.dataset.autoplay;
    if (poster) {
      poster.addEventListener("click", this.loadContent.bind(this));

      if (autoplay === "true") {
        //this.loadContent.call(this)
        poster.dispatchEvent(new Event("click"));
      }
    } else {
      this.addObserver();
    }
  }

  addObserver() {
    if ('IntersectionObserver' in window === false) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent(false, false, 'observer');
          observer.unobserve(this);
        }
      });
    }, { rootMargin: '0px 0px 0px 0px' });

    observer.observe(this);
  }

  loadContent(focus = true, pause = false, loadTrigger = 'click') {
    if (pause) window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      this.loadTrigger = loadTrigger;

      const autoplayButtons = this.querySelector(".autoplay-controls")

      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );

      if (autoplayButtons) {
        autoplayButtons.addEventListener("click", function() {
          const play = autoplayButtons.querySelector(".play-btn")
          const pause = autoplayButtons.querySelector(".pause-btn")
  
          if (play.classList.contains("hide")) {
            deferredElement.pause()
            pause.classList.add("hide")
            play.classList.remove("hide")
          } else {
            deferredElement.play()
            pause.classList.remove("hide")
            play.classList.add("hide")
          }
        })
      }

      const isProductCard = deferredElement && deferredElement.closest('.product-card');
      if (isProductCard) deferredElement.play();
      if (focus) deferredElement.focus();
    }
  }
}
customElements.define("deferred-media", DeferredMedia);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.closeEvent = new Event("close");
    this.openEvent = new Event("open");

    let closeArea = this.querySelector('.hidden-modal-close-area');

    if ( closeArea != null) {
      closeArea.addEventListener('click', this.closeModal.bind(this))
    }


    this.querySelector('[id^="ModalClose-"]') &&
      this.querySelector('[id^="ModalClose-"]').addEventListener(
        "click",
        this.hide.bind(this)
      );
    this.addEventListener("keyup", (e) => {
      if (e.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        // if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide()
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target.nodeName === "MODAL-DIALOG") this.hide();
      });
    }

    this.scrollbarWidth = getScrollbarWidth();
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    const popup = this.querySelector(".template-popup");
    this.openedBy = opener;
    document.body.classList.add("prevent-scroll");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    this.dispatchEvent(this.openEvent);
    document.body.style.paddingRight = this.scrollbarWidth + "px";
  }

  closeModal() {
    this.removeAttribute("open");
      this.removeAttribute("closing");
      document.body.classList.remove("prevent-scroll");
  }

  hide() {
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    this.setAttribute("closing", "true");
    setTimeout(() => {
      this.removeAttribute("open");
      this.removeAttribute("closing");
      document.body.classList.remove("prevent-scroll");
      this.dispatchEvent(this.closeEvent);
      document.body.style.paddingRight = "";
    }, 300);
  }
}

customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.dataset.modal);
      if (modal) modal.show(button);
    });
  }
}

customElements.define("modal-opener", ModalOpener);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.productId = this.dataset.productId;

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );

    this.input.addEventListener("change", () => {
      window.FoxThemeEvents.emit(
        `${this.productId}__QUANTITY_CHANGE`,
        this.input.value,
        this
      );
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define("quantity-input", QuantityInput);

class Drawer extends HTMLElement {
  constructor() {
    super();
    this.closeEvent = new Event("close");
    this.openEvent = new Event("open");

    this.opened = false;

    this.querySelector('[id^="DrawerClose-"]') &&
      this.querySelector('[id^="DrawerClose-"]').addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          this.closeDrawer();
        }
      );
    this.addEventListener("keyup", (e) => {
      if (e.code.toUpperCase() === "ESCAPE") this.closeDrawer();
    });

    this.elementToFocus =
      this.querySelector('[tabindex="-1"]') ||
      this.querySelector('input:not([type="hidden"])');
    if (this.dataset.elementToFocus)
      this.elementToFocus = this.querySelector(this.dataset.elementToFocus);
    // this.scrollbarWidth = getScrollbarWidth();

    // used for search ADA
    this.psActiveIndex = -1;
    this.psObserver = null;

    // Bind handlers once (so add/removeEventListener works)
    this.onSearchInputKeyDown = this.onSearchInputKeyDown.bind(this);
    this.onPredictiveResultsMutate = this.onPredictiveResultsMutate.bind(this);
    this.onDrawerSearchKeyDownCapture = this.onDrawerSearchKeyDownCapture.bind(this);
  }

  connectedCallback() {
    if (!this.originalParent) {
      this.originalParent = this.parentElement;
      this.originalNextSibling = this.nextElementSibling;
    }

    if (!this.boundHandleResize) {
      this.boundHandleResize = this.handleResize.bind(this);
    }

    if (!this.mediaQuery) {
      this.mediaQuery = window.matchMedia('(max-width: 767px)');
    }

    this.mediaQuery.addEventListener('change', this.boundHandleResize);

    this.handleResize(this.mediaQuery);
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.boundHandleResize);
  }

  handleResize(e) {
    // toggles data-can-move attribute based on media query match
    // true - on mobile
    // false - on desktop
    const isMobile = e.matches;

    if (isMobile && !this.moved) {
      this.moved = true;
      document.body.appendChild(this);
      return;
    }

    if (!isMobile && this.moved) {
      this.moved = false;

      if (this.originalParent) {
        this.originalParent.insertBefore(this, this.originalNextSibling);
      }
    }
  }

   isSearchDrawer() {
    return this.id === 'Drawer-Search';
  }

  getSearchInput() {
    return this.querySelector('#f-search-drawer-input');
  }

  getDrawerScroller() {
    return this.querySelector('.f-drawer__scroller') || this;
  }

  getPredictiveOptions() {
    const scroller = this.getDrawerScroller();

    const liveRoot =
      scroller.querySelector('#f-predictive-search-results') ||
      scroller.querySelector('[data-predictive-search]');

    const recRoot = scroller.querySelector('.f-predictive-search__recommendations');

    const collectOptions = (root) => {
      if (!root) return [];
      const options = [];
      root.querySelectorAll('[role="listbox"]').forEach((lb) => {
        lb.querySelectorAll('a[tabindex="-1"], button[tabindex="-1"]').forEach((el) => {
          options.push(el);
        });
      });
      return options;
    };

    if (this.shouldUseLiveResults()) {
      return collectOptions(liveRoot);
    } else {
      return collectOptions(recRoot);
    }
  }


  getAriaNodeForOption(optionEl) {
    return optionEl.closest('[role="option"]') || optionEl;
  }

  ensureId(node) {
    if (!node.id) node.id = `ps-opt-${Math.random().toString(16).slice(2)}`;
    return node.id;
  }

  clearPredictiveActive() {
    const input = this.getSearchInput();
    if (input) input.removeAttribute('aria-activedescendant');

    this.psActiveIndex = -1;

    // Clear aria-selected + highlight class
    this.getPredictiveOptions().forEach((el) => {
      el.classList.remove('is-active-option');
      const li = el.closest('[role="option"]');
      if (li) li.setAttribute('aria-selected', 'false');
    });
  }

  setPredictiveActive(index) {
    const input = this.getSearchInput();
    if (!input) return;

    const options = this.getPredictiveOptions();
    if (!options.length) return;

    this.psActiveIndex = Math.max(0, Math.min(index, options.length - 1));

    // reset previous states
    options.forEach((el) => {
      el.classList.remove('is-active-option');
      const li = el.closest('[role="option"]');
      if (li) li.setAttribute('aria-selected', 'false');
    });

    const activeEl = options[this.psActiveIndex];
    const ariaNode = this.getAriaNodeForOption(activeEl);

    // update aria-selected on the LI if present
    if (ariaNode.matches('[role="option"]')) {
      ariaNode.setAttribute('aria-selected', 'true');
    }

    // set activedescendant on the input
    input.setAttribute('aria-activedescendant', this.ensureId(ariaNode));

    // optional highlight hook
    activeEl.classList.add('is-active-option');

    // keep visible inside scroller
    ariaNode.scrollIntoView({ block: 'nearest' });
  }

  activatePredictiveActive() {
    const options = this.getPredictiveOptions();
    if (!options.length) return;
    if (this.psActiveIndex < 0) return;

    options[this.psActiveIndex].click();
  }

  onSearchInputKeyDown(e) {
    if (!this.isOpen() || !this.isSearchDrawer()) return;

    const options = this.getPredictiveOptions();
    if (!options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setPredictiveActive(this.psActiveIndex + 1);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setPredictiveActive(this.psActiveIndex - 1);
      return;
    }

    if (e.key === 'Enter') {
      if (this.psActiveIndex >= 0) {
        e.preventDefault();
        this.activatePredictiveActive();
      }
      return;
    }

    if (e.key === 'Escape') {
      this.clearPredictiveActive();
    }
  }

  onPredictiveResultsMutate() {
    // reset active state
    this.clearPredictiveActive();
    this.normalizeSearchDrawerFocusTargets();
  }

  normalizeSearchDrawerFocusTargets() {
    if (!this.isSearchDrawer()) return;
    this.querySelectorAll('[data-predictive-search], #f-predictive-search-results').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
  }

  onDrawerSearchKeyDownCapture(e) {
    if (!this.isOpen() || !this.isSearchDrawer()) return;

    const input = this.getSearchInput();
    if (!input) return;

    if (e.target !== input) return;

    const options = this.getPredictiveOptions();
    if (!options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setPredictiveActive(this.psActiveIndex + 1);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setPredictiveActive(this.psActiveIndex - 1);
      return;
    }

    if (e.key === 'Enter') {
      if (this.psActiveIndex >= 0) {
        e.preventDefault();
        this.activatePredictiveActive();
      }
      return;
    }

    if (e.key === 'Escape') {
      this.clearPredictiveActive();
    }
  }

  setupPredictiveSearchKeyboard() {
    if (!this.isSearchDrawer()) return;

    this.addEventListener('keydown', this.onDrawerSearchKeyDownCapture, true);
    this.normalizeSearchDrawerFocusTargets();

    const scroller = this.getDrawerScroller();
    const psRoot = this.getPredictiveSearchRoot();
    const resultsRoot =
      this.querySelector('#f-predictive-search-results') ||
      this.querySelector('[data-predictive-search]') ||
      scroller;

    if (!this.psObserver) {
      this.psObserver = new MutationObserver(this.onPredictiveResultsMutate);

      // Watch injected results changes
      this.psObserver.observe(resultsRoot, { childList: true, subtree: true });

      if (psRoot) {
        this.psObserver.observe(psRoot, { attributes: true, attributeFilter: ['results'] });
      }
    }
  }

  teardownPredictiveSearchKeyboard() {
    if (!this.isSearchDrawer()) return;

    this.removeEventListener('keydown', this.onDrawerSearchKeyDownCapture, true);

    if (this.psObserver) {
      this.psObserver.disconnect();
      this.psObserver = null;
    }

    this.clearPredictiveActive();
  }

  getPredictiveSearchRoot() {
    return this.querySelector('predictive-search');
  }

  shouldUseLiveResults() {
    const ps = this.getPredictiveSearchRoot();
    if (!ps) return false;

    // Theme uses predictive-search[results=true]
    const val = ps.getAttribute('results');
    return val === 'true' || val === '' || ps.results === true;
  }

  isOpen() {
    return this.hasAttribute("open");
  }

  onBodyClick(e) {
    if (e.target.classList.contains("f-drawer__overlay"))
      this.closeDrawer(false);
  }

  openDrawer(opener) {
    this.openedBy = opener;
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener("click", this.onBodyClickEvent);
    document.body.classList.add("prevent-scroll");
    this.setAttribute("open", "");

    if ( !this.hasAttribute("tabindex") ) {
      this.setAttribute("tabindex", "-1");
    }

    let focusTarget =
      (this.dataset.elementToFocus && this.querySelector(this.dataset.elementToFocus)) ||
      this.querySelector('[tabindex="-1"]') ||
      this.querySelector('input:not([type="hidden"])') ||
      this;

    focusTarget.focus();

    this.setupPredictiveSearchKeyboard();

    this.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('focusin', this.onFocusIn);
    if (this.isSearchDrawer()) {
      document.addEventListener('keydown', this.onSearchDrawerKeyDownCapture, true);
    }

    this.dispatchEvent(this.openEvent);
    // document.body.style.paddingRight = this.scrollbarWidth + "px";
  }

  closeDrawer(focusToggle = true) {
    this.removeAttribute("open");
    document.body.removeEventListener("click", this.onBodyClickEvent);
    document.body.classList.remove("prevent-scroll");
    this.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('focusin', this.onFocusIn);
    if (this.isSearchDrawer()) {
      document.removeEventListener('keydown', this.onSearchDrawerKeyDownCapture, true);
    }

    // Restore focus to opener
    if (focusToggle && this.openedBy?.focus) this.openedBy.focus();

    this.teardownPredictiveSearchKeyboard();
    this.dispatchEvent(this.closeEvent);
    // document.body.style.paddingRight = "";
  }

  getFocusableElements() {
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusables = Array.from(this.querySelectorAll(selectors)).filter(
      (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
    );

    if (!this.isSearchDrawer()) return focusables;

    return focusables.filter(
      (el) => !el.matches('[data-predictive-search], #f-predictive-search-results, .f-drawer__scroller')
    );
  }

  onKeyDown = (e) => {
    if ( !this.isOpen() ) return;
    if ( this.isSearchDrawer() ) return;

    if ( e.key !== 'Tab' ) return;
    this.trapTabWithinDrawer(e);
  };

  onSearchDrawerKeyDownCapture = (e) => {
    if (!this.isSearchDrawer() || !this.isOpen()) return;
    if (e.key !== 'Tab') return;
    this.trapTabWithinDrawer(e);
  };

  trapTabWithinDrawer(e) {
    const focusables = this.getFocusableElements();
    if ( !focusables.length ) {
      e.preventDefault();
      this.focus();
      return;
    }

    const active = document.activeElement;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const currentIndex = focusables.indexOf(active);

    if ( e.shiftKey ) {
      if ( currentIndex <= 0 ) {
        e.preventDefault();
        (currentIndex === -1 ? last : focusables[currentIndex - 1]).focus();
      }
      return;
    }

    if ( currentIndex === -1 || currentIndex === focusables.length - 1 ) {
      e.preventDefault();
      (currentIndex === -1 ? first : focusables[0]).focus();
    }
  }

  onFocusIn = (e) => {
    if (!this.isOpen()) return;

    if (this.isSearchDrawer() && e.target?.matches?.('.f-drawer__scroller, [data-predictive-search], #f-predictive-search-results')) {
      const input = this.getSearchInput();
      const fallback = this.querySelector('.f-drawer__close') || this;
      (input || fallback).focus();
      return;
    }

    // If focus somehow lands outside the drawer while it's open, bring it back in
    if ( !this.contains(e.target) ) {
      const focusables = this.getFocusableElements();
      (focusables[0] || this).focus();
    }
  };
}

customElements.define("drawer-component", Drawer);

customElements.define(
  "drawer-opener",
  class DrawerOpener extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.disabled === "true") return;
      const button =
        this.querySelector("button") || this.querySelector("[data-opener]");
      if (!button) return;
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const drawer = document.querySelector(this.dataset.drawer);
        if (drawer) drawer.openDrawer(button);
      });
    }
  }
);

customElements.define(
  "drawer-closer",
  class DrawerCloser extends HTMLElement {
    constructor() {
      super();
      const drawer = this.closest("drawer-component");

      if (drawer) {
        // Make it keyboard focusable
        if (!this.hasAttribute("tabindex")) {
          this.setAttribute("tabindex", "0");
        }

        // Add ARIA role
        if (!this.hasAttribute("role")) {
          this.setAttribute("role", "button");
        }

        this.addEventListener("click", (e) => {
          e.preventDefault();
          drawer.closeDrawer();
        });

        this.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault(); 
            drawer.closeDrawer();
          }
        });
      }
    }
  }
);

customElements.define(
  "cart-note",
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        "change",
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${FoxThemeSettings.routes.cart_update_url}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: `application/json`,
            },
            ...{ body },
          });
        }, 300)
      );
      this.collapsible = this.querySelector("collapsible-tab");
      this.querySelector(".btn-cancel").addEventListener(
        "click",
        this.handleClose.bind(this)
      );
    }

    handleClose(e) {
      e.preventDefault();
      if (this.collapsible) this.collapsible.close();
    }
  }
);

customElements.define("cart-delivery-time",
  class CartDeliveryTime extends HTMLElement {
    constructor() {
      super();
      this.collapsible = this.querySelector("collapsible-tab");
      this.deliveryTimeElm = this.querySelector("[name='attributes[Delivery time]']");
      this.errorWrapper = this.querySelector(".f-cart-block-message-error");
      this.deliveryCodeKey = "zest-delivery-code";
      this.querySelector(".btn-cancel").addEventListener(
        "click",
        this.handleClose.bind(this)
      );
      this.querySelector(".btn-save").addEventListener(
        "click",
        this.handleSave.bind(this)
      )
      const today = new Date().toISOString().slice(0, 16);
      if (this.deliveryTimeElm) this.deliveryTimeElm.min = today;
    }
    handleSave(e) {
      e.preventDefault();
      const time = this.deliveryTimeElm.value;
      const currentTime = Date.parse(time);
      if (currentTime > Date.now()) {
        localStorage.setItem(this.deliveryCodeKey, time);
        const body = JSON.stringify({ attributes: { 'Delivery time': time }});
        fetch(`${FoxThemeSettings.routes.cart_update_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: `application/json`,
          },
          ...{ body },
        });
        this.handleClose(e);
      } else if (time == '') {
        localStorage.removeItem(this.deliveryCodeKey);
        window.FoxTheme.Notification.show({
					target: this.errorWrapper,
					method: 'appendChild',
					type: 'warning',
					message: window.FoxThemeStrings.noSelectDeliveryTime
				});
      } else {
        localStorage.removeItem(this.deliveryCodeKey);
        window.FoxTheme.Notification.show({
					target: this.errorWrapper,
					method: 'appendChild',
					type: 'warning',
					message: window.FoxThemeStrings.invalidDeliveryTime
				});
      }
    }
    handleClose(e) {
      e.preventDefault();
      if (this.collapsible) this.collapsible.close();
    }
  }
);

customElements.define(
  "product-modal",
  class ProductModal extends ModalDialog {
    constructor() {
      super();
    }

    connectedCallback() {
      this.slider = this.querySelector("flickity-component");
      this.mediaContent = this.querySelector(".media-modal__content");
    }

    hide() {
      super.hide();
      this.mediaContent.setAttribute("data-media-loading", "");
    }

    show(opener) {
      super.show(opener);
      if (this.slider) {
        setTimeout(() => {
          this.slider.update();
          this.showActiveMedia();
          this.slider.focus();
        }, 300);
      }
    }

    showActiveMedia() {
      const activeMedia = this.querySelector(
        `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`
      );
      if (activeMedia) {
        const activeIndex = Number(activeMedia.dataset.index);
        this.slider.select(activeIndex, false, true);
        this.mediaContent.removeAttribute("data-media-loading");
      }
    }
  }
);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }

  open() {
    this.mainDetailsToggle.setAttribute("open", '');
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", true);
  }
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = generateDomFromString(text);
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
            __reInitTooltip(this);

            document.dispatchEvent(
              new CustomEvent("recommendationsLoaded", {} ),
            );
          }

          if (html.querySelector(".product-card")) {
            this.classList.add("product-recommendations--loaded");
          } else {
            this.classList.add("f-hidden");
          }


        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class FProgressBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    setTimeout(() => {
      const quantity = parseInt(this.dataset.quantity);
      this.style.setProperty(
        "--progress-bar-width",
        `${(quantity * 100) / 50}%`
      );
    }, 1000);
  }
}
customElements.define("f-progress-bar", FProgressBar);
class VideoComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.parentSelector = this.dataset.parent || ".f-video-wrapper";
    this.parent = this.closest(this.parentSelector);

    switch (this.dataset.type) {
      case "youtube":
        this.initYoutubeVideo();
        break;

      case "vimeo":
        this.initVimeoVideo();
        break;

      case "mp4":
        this.initMp4Video();
        break;
    }
  }

  initYoutubeVideo() {
    this.setAsLoading();
    this.loadScript("youtube").then(this.setupYoutubePlayer.bind(this));
  }

  initVimeoVideo() {
    this.setAsLoading();
    this.loadScript("vimeo").then(this.setupVimeoPlayer.bind(this));
  }

  initMp4Video() {
    const player = this.querySelector("video");

    if (player) {
      const promise = player.play();

      // Edge does not return a promise (video still plays)
      if (typeof promise !== "undefined") {
        promise
          .then(function () {
            // playback normal
          })
          .catch(function () {
            player.setAttribute("controls", "");
          });
      }
    }
  }

  loadScript(videoType) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src =
        videoType === "youtube"
          ? "//www.youtube.com/iframe_api"
          : "//player.vimeo.com/api/player.js";
    });
  }

  setAsLoading() {
    this.parent.setAttribute("loading", true);
  }

  setAsLoaded() {
    this.parent.removeAttribute("loading");
    this.parent.setAttribute("loaded", true);
  }

  setupYoutubePlayer() {
    const videoId = this.dataset.videoId;
    const playerInterval = setInterval(() => {
      if (window.YT) {
        window.YT.ready(() => {
          const element = document.createElement("div");
          this.appendChild(element);

          this.player = new YT.Player(element, {
            videoId: videoId,
            playerVars: {
              showinfo: 0,
              controls: false,
              fs: 0,
              rel: 0,
              height: "100%",
              width: "100%",
              iv_load_policy: 3,
              html5: 1,
              loop: 1,
              playsinline: 1,
              modestbranding: 1,
              disablekb: 1,
            },
            events: {
              onReady: this.onYoutubeReady.bind(this),
              onStateChange: this.onYoutubeStateChange.bind(this),
            },
          });
          clearInterval(playerInterval);
        });
      }
    }, 50);
  }

  onYoutubeReady() {
    this.iframe = this.querySelector("iframe"); // iframe once YT loads
    this.iframe.setAttribute("tabindex", "-1");

    if (typeof this.player.mute === "function") this.player.mute();
    if (typeof this.player.playVideo === "function") this.player.playVideo();

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.youtubePlay();
          } else {
            this.youtubePause();
          }
        });
      },
      { rootMargin: "0px 0px 50px 0px" }
    );

    observer.observe(this.iframe);
  }

  onYoutubeStateChange(event) {
    switch (event.data) {
      case -1: // unstarted
        // Handle low power state on iOS by checking if
        // video is reset to unplayed after attempting to buffer
        if (this.attemptedToPlay) {
          this.setAsLoaded();
          // this.closest('.banner').classList.add('video-interactable');
        }
        break;
      case 0: // ended, loop it
        this.youtubePlay();
        break;
      case 1: // playing
        this.setAsLoaded();
        break;
      case 3: // buffering
        this.attemptedToPlay = true;
        break;
    }
  }

  youtubePlay() {
    if (this.player && typeof this.player.playVideo === "function") {
      this.player.playVideo();
    }
  }

  youtubePause() {
    if (this.player && typeof this.player.pauseVideo === "function") {
      this.player.pauseVideo();
    }
  }

  setupVimeoPlayer() {
    const videoId = this.dataset.videoId;

    const playerInterval = setInterval(() => {
      if (window.Vimeo) {
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: false,
          controls: false,
          loop: true,
          height: "100%",
          width: "100%",
        });
        this.player.ready().then(this.onVimeoReady.bind(this));
        clearInterval(playerInterval);
      }
    }, 50);
  }

  onVimeoReady() {
    this.iframe = this.querySelector("iframe");
    this.iframe.setAttribute("tabindex", "-1");

    this.player.setMuted(true);

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.vimeoPlay();
          } else {
            this.vimeoPause();
          }
        });
      },
      { rootMargin: "0px 0px 50px 0px" }
    );

    observer.observe(this.iframe);
  }

  vimeoPlay() {
    if (this.player && typeof this.player.play === "function") {
      this.player.play();
    }
  }

  vimeoPause() {
    if (this.player && typeof this.player.pause === "function") {
      this.player.pause();
    }
  }
}
customElements.define("video-component", VideoComponent);

if (!customElements.get("f-scrolling-promotion")) {
  class FScrollingPromotion extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.promotion = this.querySelector(".f-promotion");
      this.init();
    }

    init() {
      if (this.childElementCount === 1) {
        this.promotion.classList.add("f-promotion--animated");

        for (let index = 0; index < 10; index++) {
          this.clone = this.promotion.cloneNode(true);
          this.clone.setAttribute("aria-hidden", true);
          this.appendChild(this.clone);
        }

        // pause when out of view
        const observer = new IntersectionObserver(
          (entries, _observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.scrollingPlay();
              } else {
                this.scrollingPause();
              }
            });
          },
          { rootMargin: "0px 0px 50px 0px" }
        );

        observer.observe(this);
      }
    }

    scrollingPlay() {
      this.classList.remove("f-scrolling-promotion--paused");
    }

    scrollingPause() {
      this.classList.add("f-scrolling-promotion--paused");
    }
  }

  customElements.define("f-scrolling-promotion", FScrollingPromotion);
}

class ImageComparisonSlider extends HTMLElement {
  constructor() {
    super();
    this.active = false;
    this.button = this.querySelector("button");
    this.horizontal = this.dataset.layout === "horizontal";
    this.customContent = this.closest('[data-section-type="f-custom-content"]');

    if (this.hasAttribute("data-animation")) {
      intersecting({
        element: this.querySelector('.f-image-comparison-slider__animation'),
        callback: this.animation.bind(this),
        threshold: 0
      })
    } else {
      this.init();
    }
  }

  init() {
    this.button.addEventListener(
      "touchstart",
      this.handleTouchStart.bind(this),
      {passive: true}
    );
    document.body.addEventListener("touchend", this.handleTouchEnd.bind(this));
    document.body.addEventListener(
      "touchmove",
      this.handleMouseMove.bind(this)
    );

    this.button.addEventListener("mousedown", this.handleTouchStart.bind(this));
    document.body.addEventListener("mouseup", this.handleTouchEnd.bind(this));
    document.body.addEventListener(
      "mousemove",
      this.handleMouseMove.bind(this)
    );
  }

	animation() {
		const animation = this.hasAttribute('data-animation')
		if (!animation) return;
		this.setAttribute('is-visible', '');
    this.classList.add('f-animating');
    setTimeout(() => {
      this.classList.remove('f-animating');
    }, 1e3);
    this.init();
	}

  handleTouchStart() {
    const scrollBarWidth = getScrollbarWidth();
    this.customContent && this.customContent.classList.remove('relative');
    document.body.style.setProperty("padding-right", `${scrollBarWidth}px`);
    document.body.style.overflow = "hidden";
    this.active = true;
    this.classList.add("dragging");
  }

  handleTouchEnd() {
    document.body.style.removeProperty("padding-right");
    this.customContent && this.customContent.classList.add('relative');
    this.active = false;
    this.classList.remove("dragging");
    document.body.style.overflow = "";
  }

  handleMouseMove(e) {
    if (!this.active) return;

    const event = (e.touches && e.touches[0]) || e;
    const x = this.horizontal
      ? event.pageX - this.offsetLeft
      : event.pageY - this.offsetTop;

    this.handleChange(x);
  }

  handleChange(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;

    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, Math.min(x, max));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty("--percent", mousePercent + "%");
  }
}
customElements.define("f-image-comparison-slider", ImageComparisonSlider);

if (!customElements.get("component-inventory-status")) {
  class InventoryStatus extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        inventory: "[data-product-inventory]",
        incomingInventory: "[data-incoming-inventory]",
      };
    }

    connectedCallback() {
      this.productId = this.dataset.productId;
      let inventoryEl = this.querySelector(this.selectors.inventory);
      if (inventoryEl) {
        this.inventory = true;
        this.inventoryThreshold = inventoryEl.dataset.threshold;
        window.FoxThemeEvents.subscribe(
          `${this.productId}__VARIANT_CHANGE`,
          async (variant) => {
            this.updateInventoryStatus(variant);
          }
        );
      }
    }

    updateInventoryStatus(variant) {
      if (
        !variant ||
        !variant.inventory_management ||
        variant.inventory_policy === "continue"
      ) {
        this.toggleInventoryQuantity(variant, false);
        this.toggleIncomingInventory(false);
        return;
      }

      if (
        variant.inventory_management === "shopify" &&
        window.FoxInventories &&
        window.FoxInventories[this.productId]
      ) {
        let variantInventoryObject =
          window.FoxInventories[this.productId][variant.id];

        // Hide stock if policy is continue
        if (variantInventoryObject.policy === "continue") {
          this.toggleInventoryQuantity(variant, false);
          this.toggleIncomingInventory(false);
          return;
        }

        let quantity = variantInventoryObject.quantity,
          showInventory = true,
          showIncomingInventory = false;

        if (quantity <= 0 || quantity > this.inventoryThreshold) {
          showInventory = false;
        }

        this.toggleInventoryQuantity(variant, showInventory, quantity);

        if (
          variantInventoryObject.incoming === "true" &&
          quantity <= this.inventoryThreshold
        ) {
          showIncomingInventory = true;
        }

        this.toggleIncomingInventory(
          showIncomingInventory,
          variant.available,
          variantInventoryObject.next_incoming_date
        );
      }
    }

    toggleInventoryQuantity(variant, show, qty) {
      if (!this.inventory) show = false;

      const el = document.querySelector(this.selectors.inventory);
      const fInventory = el.closest(
        ".f-product-single__block-inventory-status"
      );
      const progressEl = document.querySelector("f-progress-bar");
      const showProgress = progressEl.dataset.show === "true";

      if (parseInt(qty) <= parseInt(this.inventoryThreshold)) {
        el.parentNode.classList.add("f-inventory--low");
        el.textContent = window.FoxThemeStrings.stockLabel.replace(
          "[count]",
          qty
        );
        if (showProgress) {
          el.parentNode
            .querySelector(".f-inventory--icon")
            .classList.add("hidden");
          progressEl.style.display = "block";
          progressEl.dataset.quantity = qty;
          progressEl.init();
        }
      } else {
        el.parentNode.classList.remove("f-inventory--low");
        el.textContent = window.FoxThemeStrings.inStockLabel;
        if (showProgress) {
          progressEl.style.display = "none";
          el.parentNode
            .querySelector(".f-inventory--icon")
            .classList.remove("hidden");
        }
      }

      if (variant && variant.available) {
        el.parentNode.classList.remove("hidden");
        if (fInventory) {
          fInventory.classList.remove("hidden");
        }
      } else {
        el.parentNode.classList.add("hidden");
        if (showProgress) progressEl.style.display = "none";
        if (fInventory) {
          fInventory.classList.add("hidden");
        }
      }
    }

    toggleIncomingInventory(show, available, date) {
      const el = document.querySelector(this.selectors.incomingInventory);
      const fInventory = el.closest(
        ".f-product-single__block-inventory-status"
      );

      if (!fInventory) return;

      const textEl = el.querySelector(".f-js-incoming-text");

      if (show) {
        let string = available
          ? window.FoxThemeStrings.willNotShipUntil.replace("[date]", date)
          : window.FoxThemeStrings.willBeInStockAfter.replace("[date]", date);
        if (!date) {
          string = window.FoxThemeStrings.waitingForStock;
        }

        el.classList.remove("hidden");
        if (fInventory) {
          fInventory.classList.remove("hidden");
        }
        textEl.textContent = string;
      } else {
        el.classList.add("hidden");
      }
    }
  }

  customElements.define("component-inventory-status", InventoryStatus);
}

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();

    // Save product Id
    if (isStorageSupported("local")) {
      const productId = parseInt(this.dataset.productId);
      const cookieName = "zest-recently-viewed";
      const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");

      // Check current product already exists, if not push to array
      if (!items.includes(productId)) {
        items.unshift(productId);
      }

      // Save to localStorage
      window.localStorage.setItem(cookieName, JSON.stringify(items.slice(0, 20)));
    }
  }
}
customElements.define("product-recently-viewed", ProductRecentlyViewed);

class ProductQuickAdd extends HTMLElement {
  constructor() {
    super();

    this.rootElement = this;
    this.relatedProductId = this.dataset.productId;
    this.relatedProductJson = null;
    this.Notification = window.FoxTheme.Notification;
    this.notificationType = 'toast';
    this.cartDrawer = document.querySelector('cart-drawer')
    this.mainCart = document.querySelector('cart-items');
    this.type = 'product-card';
    this.errorWrapper = this.type === 'product-card' ? this.closest('.product-card') : this.domNodes.errorWrapper;
    this.animationDelay = 500;
    
    if ( window.useNewQuickaddSystem ) {
      this.init();
    } else {
      this.destroy();
    }
    
  }

  init() {
    // console.log( 'INIT ProductQuickAdd');
    const toggleQuickAddViewButton = this.rootElement.querySelector('.new-quickshop-button');
    const sizingOptionButtons = this.rootElement.querySelectorAll('.radio-size-option');
    const productCard = this.rootElement.closest('.product-card');
    const productJson = this.rootElement.querySelector(`#product-json-${this.relatedProductId}`);
    if ( productJson != null ) {
      this.relatedProductJson = JSON.parse( productJson.innerHTML );
    }
    
    if ( toggleQuickAddViewButton != null ) {
      toggleQuickAddViewButton.addEventListener( 'click' , this.handleQuickAddToggle.bind( this ) );
    }

    for ( let i = 0; i < sizingOptionButtons.length; i++ ) {
      sizingOptionButtons[i].addEventListener( 'click' , this.handleAddingProductToCart.bind( this ) );
    }

    if ( productCard != null ) {
      /* REMOVE HIDDEN TEST */
      // productCard.addEventListener( 'mouseleave' , this.resetMenu.bind( this ) );
    }
  }

  destroy() {
    this.rootElement.classList.add('hidden');
    
    const rootNode = this.rootElement.closest('.f-column');
    const productCardAnchor = rootNode.querySelector('.product-card__link.desktop-only');
    const productCardButton = rootNode.querySelector('.product-card__link.mobile-only');
    const productCardActionDiv = this.rootElement.closest('.product-card__actions');
    const originalQuickviewButton = productCardActionDiv.querySelector('.hidden-desktop.btn');

    productCardActionDiv.classList.remove('mobile-hide-quick-view');
    originalQuickviewButton.classList.remove('hidden-desktop');

    productCardAnchor.classList.remove('desktop-only');
    productCardButton.remove();
  }

  handleQuickAddToggle( event ) {
    this.updateSizesAvailable();

    const sizingOptionsContainer = this.rootElement.querySelector('.quickview-size-product-options');
    const toggleQuickAddViewButton = this.rootElement.querySelector('.new-quickshop-button');
    
    if ( sizingOptionsContainer != null ) {
      sizingOptionsContainer.classList.remove('hidden');
    }

    if ( toggleQuickAddViewButton != null ) {
      toggleQuickAddViewButton.classList.add('hidden');
    }
  }

  updateSizesAvailable() {
    // console.log('updating available sizing...');

    // read the color selected
    const selectedColor = this.getSelectedColor();
    if ( selectedColor == '' ) {
      return;
    } 

    const variants = this?.relatedProductJson?.variants || [];
    const relatedVariants = [];
    for ( let i = 0; i < variants?.length; i++ ) {
      for ( let j = 0; j < variants[i].selected_options.length; j++ ) {
        if ( variants[i].selected_options[j].name == selectedColor ) {
          relatedVariants.push( variants[i] );
          break;
        }
      }
    }

    for ( let i = 0; i < relatedVariants.length; i++ ) {
      for ( let j = 0; j < relatedVariants[i].selected_options.length; j++ ) {
        const sizeOption = relatedVariants[i].selected_options[j].name; // could be a color but that query should result in null
        const sizeButtonRef = this.rootElement.querySelector(`.radio-size-option[value="${sizeOption}"]`);
        if ( sizeButtonRef != null ) {
          if ( relatedVariants[i].available ) {
            sizeButtonRef.removeAttribute( 'disabled' );
          } else {
            sizeButtonRef.setAttribute( 'disabled' , 'true' );
          }
        }
      }
    }

    return;
  }

  async handleAddingProductToCart( event ) {
    console.log('adding to cart...');
    console.log( event );

    // read the color selected
    const selectedColor = this.getSelectedColor();
    if ( selectedColor == '' ) {
      return;
    }

    // read the size selected
    const selectedSize = this.getSelectedSize();
    if ( selectedSize == '' ) {
      return;
    } 
    
    console.log( 'COLOR: ' , selectedColor );
    console.log( 'SIZE: ' , selectedSize );
    
    // get variant id based on these options
    const variants = this?.relatedProductJson?.variants || [];
    let selectedVariantId = -1;
    for ( let i = 0; i < variants?.length; i++ ) {
      let foundColor = false;
      let foundSize = false;

      for ( let j = 0; j < variants[i].selected_options.length; j++ ) {
        if ( variants[i].selected_options[j].name == selectedColor ) {
          foundColor = true;
        }

        if ( variants[i].selected_options[j].name == selectedSize ) {
          foundSize = true;
        }
      }

      if ( foundColor && foundSize ) {
        selectedVariantId = variants[i].id;
        break;
      }
    }

    try {
      const formData = new FormData();
      formData.append('id', selectedVariantId );
      formData.append('quantity', 1)

      if (this.cartDrawer) {
        formData.append(
          'sections',
          this.cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      config.body = formData;
      const {FoxThemeSettings, FoxThemeStrings} = window;
      const url = `${FoxThemeSettings.routes.cart_add_url}`;
      const response = await fetch( url , config );

      if (!response.ok) {
        let message = 'Cannot add selected item to the cart.';
        this.deselectCurrentSize();
        this.Notification.show({
          target: this.notificationType === 'toast' ? document.body : this.errorWrapper,
          method: 'appendChild',
          type: 'warning',
          message: message,
          last: 3000,
          sticky: this.notificationType === 'toast'
        })

        setTimeout(() => {
          this.Notification.removeNoti();
        } , 3000);

        return;
      }

      const results = await response.json();

      try {
        if ( window.lillapAnalytics ) {
          window.lillapAnalytics.trackQUICKSize();
        }
      } catch ( gaerror ) { 
        // do nothing
      }


      if ( typeof( triggerCollectionSurveyEvent ) != 'undefined' ) {
        try {
          const cartRes = await fetch( '/cart.js' );
          const cart = await cartRes.json();

          const uniqueCartLines = cart.items.length;
          triggerCollectionSurveyEvent( uniqueCartLines );

        } catch ( error ) {
          console.log( error );
          console.warn( 'failed to get cart...' );
        }
      }

      if (!FoxThemeSettings.cart.openDrawerWhenAdded || FoxThemeSettings.cart.cartType === 'page') {
        this.Notification.show({
          target: this.errorWrapper,
          method: 'appendChild',
          type: 'success',
          message: FoxThemeStrings.item_added,
          last: 3000,
          sticky: this.notificationType === 'toast'
        })
      }
      if (FoxThemeSettings.cart.cartType === 'page') {
        FoxThemeCartHelpers.getState().then(state => {
          FoxThemeCartHelpers.updateCartCount(state.item_count)
        })
      }
  
      window.FoxThemeEvents.emit(`ON_ITEM_ADDED`, results );
    } catch ( error ) {
      console.log( error );
      console.log( 'FAILED TO ADD ITEMS TO THE CART...');
    }
    
    // deselect current size option
    this.deselectCurrentSize();
  }

  getSelectedColor( ) {
    let results = '';

    const productCardWrapper = this.rootElement.closest('.product-card');
    if ( productCardWrapper == null ) {
      console.warn('product card element is null so sizing update did not finish');
      return results;
    }

    const selectedSwatch = productCardWrapper.querySelector('.swatch-list-item.active-color .swatch');
    if ( selectedSwatch == null ) {
      console.warn('selected swatch element is null so sizing update did not finish');
      return results;
    }

    const selectedColor = selectedSwatch.getAttribute('data-color-option');
    if ( selectedColor == null ) {
      console.warn('selected color is null so sizing update did not finish');
      return results;
    }

    results = selectedColor;

    return results;
  }

  getSelectedSize( ) {
    let results = '';

    const selectedRadioOption = this.rootElement.querySelector('.radio-size-option[name="size"]:checked');
    if ( selectedRadioOption != null ) {
      results = selectedRadioOption.value;
    }

    return results;
  }

  deselectCurrentSize() {
    const selectedRadioOption = this.rootElement.querySelector('.radio-size-option[name="size"]:checked');
    if ( selectedRadioOption != null ) {
      selectedRadioOption.checked = false;
    }

    return;
  }

  resetMenu() {
    setTimeout( () => {
      const quickAddToggle = this.rootElement.querySelector('.new-quickshop-button');
      if ( quickAddToggle != null ) {
        quickAddToggle.classList.remove('hidden');
      }
  
      const optionsList = this.rootElement.querySelector('.quickview-size-product-options');
      if ( optionsList != null ) {
        optionsList.classList.add('hidden');
      }
    } , this.animationDelay );
  }
}
customElements.define("product-quick-add", ProductQuickAdd);


class ProductCardSlider extends HTMLElement {
  constructor() {
    super();

    this.rootElement = this;
    this.relatedProductId = this.dataset.productId;
    this.infiniteSlider = ( this.dataset.infiniteSlider == 'true' ? true : false );
    this.showArrowsOnCardSlider = window.showArrowsOnCardSlider;
    this.hoverArrowsOnCardSlider = window.hoverArrowsOnCardSlider;


    // when an ab test override is set to true...
    if ( window.infiniteSliderABTest === true ) {
      this.infiniteSlider = true;
    }

    // when an ab test override is set to false...
    if ( window.infiniteSliderABTest === false ) {
      this.infiniteSlider = false;
    }


    this.productMedia = [];
    this.availableColorsBeforeViewMore = [];
    this.previousArrows = this.rootElement.querySelectorAll( '.previous' );
    this.nextArrows = this.rootElement.querySelectorAll( '.next' );
    this.productCard = this.rootElement.closest('.product-card');
    this.hoveringSystem = this.rootElement.closest('.product-card').getAttribute('data-hover-image-system');
    this.imageWrapper = this.rootElement.closest('.product-card__image-wrapper');
    this.currentMediaIndex = 0;
    this.totalMediaSlides = 0;
    this.currentSelectedColor = null;
    this.nextClick = 0;

    // console.log('this.productCard', this.productCard)
    // console.log('this.hoveringssytem', this.hoveringSystem)
    // console.log('this.imageWrapper', this.imageWrapper)
    
    this.loadProductMedia();
    this.initSliderEvents();

    if ( !window.useNewQuickaddSystem ) {
      this.rootElement.classList.add('hidden');
    }
  }

  loadProductMedia() {
    const mediaJSONEl = this.rootElement.querySelector(`#image-media-for-${this.relatedProductId}`);
    if ( mediaJSONEl != null ) {
      const mediaJSONText = mediaJSONEl.innerHTML;
      const mediaJSON = JSON.parse( mediaJSONText );

      // sort media.
        // 1) filter out any colors that are not available based on 
        // 2) order media in the same order as the color swatches.

      const availableMedia = [];
      
      // updates based on BASE COLOR FILTER - Shopify Search and Discovery + Variant Metafield BASE COLOR
      let otherColors = [];
      let filterColors = [];
      let hasFilterColors = false;
      let activeSwatchUrl = "";
      let activeSwatchFile = "";
      let activeSwatchFound = false;

      for ( let i = 0; i < mediaJSON?.available_colors_before_view_more?.length; i++ ) {
        const currentColorToCheckFor = mediaJSON.available_colors_before_view_more[i].related_variant_color_sku;

        // updates based on BASE COLOR FILTER - Shopify Search and Discovery + Variant Metafield BASE COLOR
        // based on BASE PATTERN metafield
        const productGrid = document.querySelector('#product-grid');
        if (productGrid != null) {
          const hasBaseColorAttr =
            productGrid.hasAttribute('data-active-base-color') &&
            productGrid.getAttribute('data-active-base-color').length > 0;

          const hasBasePatternAttr =
            productGrid.hasAttribute('data-active-base-pattern') &&
            productGrid.getAttribute('data-active-base-pattern').length > 0;

          if (hasBaseColorAttr || hasBasePatternAttr) {
            hasFilterColors = true;
            let baseColor = '';
            let basePattern = '';

            if (hasBaseColorAttr) {
              baseColor = productGrid.getAttribute('data-active-base-color');
            }
            if (hasBasePatternAttr) {
              basePattern = productGrid.getAttribute('data-active-base-pattern');
            }

            const swatches = productGrid.querySelectorAll('.swatch-list-item');

            let matchingSwatch = null;
            for (let i = 0; i < swatches.length; i++) {
              const swatch = swatches[i];
              const swatchBaseColor = swatch.getAttribute('data-base-color');
              const swatchBasePattern = swatch.getAttribute('data-base-pattern');
              let isMatch = false;
              
              if (baseColor !== '') {
                isMatch = swatchBaseColor === baseColor;
              } else if (basePattern !== '') {
                isMatch = swatchBasePattern === basePattern;
              }

              if (isMatch) {
                matchingSwatch = swatch;
                break;
              }
            }

            if (matchingSwatch != null) {
              const activeSwatchUrlAttr = matchingSwatch.getAttribute('data-color-image-url');
              if (activeSwatchUrlAttr != null) {
                activeSwatchUrl = activeSwatchUrlAttr;
              }
            }
          }
        }
        
        for ( let j = 0; j < mediaJSON?.media?.length; j++ ) {
          const currentImageFile = mediaJSON.media[j].image.file;

            if ( currentImageFile.includes( currentColorToCheckFor ) ) {
              if ( hasFilterColors && !activeSwatchFound ) {
                if ( activeSwatchUrl.includes( currentColorToCheckFor ) ) {
                  activeSwatchFile = currentImageFile;
                  activeSwatchFound = true;
                  this.currentSelectedColor = mediaJSON.available_colors_before_view_more[i].related_color_handle;
                }
              }
              mediaJSON.media[j].color_info = mediaJSON.available_colors_before_view_more[i];
              availableMedia.push( mediaJSON.media[j] );
            }
        }
      }


      this.availableColorsBeforeViewMore = mediaJSON.available_colors_before_view_more;
      this.productMedia = availableMedia;
      
      if ( availableMedia.length > 0 ) {
        // this.currentMediaIndex = availableMedia[0].index;
        // console.log('active swatch file', activeSwatchFile)
        // console.log('this current selected color', this.currentSelectedColor)
        this.setSliderIndexByColorClicked(activeSwatchFile)

        if ( !hasFilterColors ) {
          this.currentSelectedColor = availableMedia[0].color_info.related_color_handle;
        }
      }
      this.totalMediaSlides = availableMedia.length;
    }

    return;
  }

  initSliderEvents() {
    for ( let i = 0; i < this.previousArrows.length; i++ ) {
      if ( !this.infiniteSlider ) {
        this.previousArrows[i].setAttribute('disabled' , 'true');
      }

      // no images to go through so dont show
      if ( this.availableColorsBeforeViewMore.length <= 0 ) {
        this.previousArrows[i].classList.add( 'hidden' );
      }

      if ( this.showArrowsOnCardSlider === false ) {
        this.previousArrows[i].classList.add('hidden');
      }

      if ( this.hoverArrowsOnCardSlider === true ) {
          this.previousArrows[i].classList.add('hide-desktop'); //hide on load
          this.previousArrows[i].classList.remove('hidden'); // this style is causing it to not show on mobile
          if ( this.hoveringSystem && this.hoveringSystem.toString() == 'full-card' ) {
            this.productCard.classList.add("show-hover")
          } else {
            this.imageWrapper.classList.add("show-hover"); // when .product-card__wrapper is hovered, then removed hidden
          }
      }

      this.previousArrows[i].addEventListener('click' , this.previousImage.bind( this ) );
    }

    for ( let i = 0; i < this.nextArrows.length; i++ ) {
       // no images to go through so dont show
      if ( this.availableColorsBeforeViewMore.length <= 0 ) {
        this.nextArrows[i].classList.add( 'hidden' );
      }

      if ( this.showArrowsOnCardSlider === false ) {
        this.nextArrows[i].classList.add('hidden');
      }

      if ( this.hoverArrowsOnCardSlider === true ) {
        this.nextArrows[i].classList.add('hide-desktop'); //hide on load
        this.nextArrows[i].classList.remove('hidden'); // this style is causing it to not show on mobile
       if ( this.hoveringSystem && this.hoveringSystem.toString() == 'full-card' ) {
            this.productCard.classList.add("show-hover")
          } else {
            this.imageWrapper.classList.add("show-hover"); // when .product-card__wrapper is hovered, then removed hidden
          }
      }

      this.nextArrows[i].addEventListener('click' , this.nextImage.bind( this ) );
    }

    return;
  }

  previousImage( event ) {
    //disable hover when slider is used
    const pCard = event.target.closest(".product-card");
    let card = null;

    if (pCard) {
      const hoverImageSystemType = pCard.getAttribute('data-hover-image-system')
      const pCardWrapper = pCard.querySelector(".product-card__wrapper");
      const imageWrapper = pCard.querySelector(".product-card__image-wrapper");

      if ( pCardWrapper ) {
        card = pCardWrapper;
      }
    
      if ( pCardWrapper && imageWrapper && hoverImageSystemType == 'image-only' ) {
         card = imageWrapper;
      }
      
    }
    
    if ( card ) {
      card.classList.remove("enable-image-hover");
    }

    let nextIndex = this.currentMediaIndex - 1;
    // console.log( 'previous image...' );
    // console.log( 'CURRENT INDEX: ' , this.currentMediaIndex );
    // console.log( 'TOTAL SLIDES (BASE 0): ' , this.totalMediaSlides - 1 );
    // console.log( 'NEXT POTENTIAL INDEX:' , nextIndex );

    if ( this.infiniteSlider ) {
      if ( nextIndex < 0 ) {
        nextIndex = this.totalMediaSlides - 1;
      }
    } 
    else {
      // when on the first slide, disable previous
      if ( nextIndex == 0 ) {
        for ( let i = 0; i < this.previousArrows.length; i++ ) {
          this.previousArrows[i].setAttribute('disabled' , 'true');
        }
      }

      if ( nextIndex != this.totalMediaSlides - 1 ) {
        for ( let i = 0; i < this.nextArrows.length; i++ ) {
          this.nextArrows[i].removeAttribute('disabled');
        }
      }
    }

    this.currentMediaIndex = nextIndex;
    this.updateSlideImage();
    this.updateSelectedColorBasedOnSlideImage();
    this.updateUrlOnButton();

    return;
  }

  nextImage( event ) {
    //disabled hover when slider is used
    const pCard = event.target.closest(".product-card");
    let card = null;

    if (pCard) {
      const hoverImageSystemType = pCard.getAttribute('data-hover-image-system')
      const pCardWrapper = pCard.querySelector(".product-card__wrapper");
      const imageWrapper = pCard.querySelector(".product-card__image-wrapper");

      if ( pCardWrapper ) {
        card = pCardWrapper;
      }
    
      if ( pCardWrapper && imageWrapper && hoverImageSystemType == 'image-only' ) {
         card = imageWrapper;
      }
      
    }
    
    if ( card ) {
      card.classList.remove("enable-image-hover");
    }

    
    let nextIndex;

    if (this.nextClick === 0) {
      nextIndex = this.currentMediaIndex + 2;
    } else {
      nextIndex = this.currentMediaIndex + 1;
    }
    // console.log( 'next image...' );
    // console.log( 'CURRENT INDEX: ' , this.currentMediaIndex );
    // console.log( 'TOTAL SLIDES (BASE 0): ' , this.totalMediaSlides - 1 );
    // console.log( 'NEXT POTENTIAL INDEX:' , nextIndex );

    if ( this.infiniteSlider ) {
      if ( nextIndex > this.totalMediaSlides - 1 ) {
        nextIndex = 0;
      }
    } 
    else {
      // when on the last slide, disable next
      if ( nextIndex == this.totalMediaSlides - 1 ) {
        for ( let i = 0; i < this.nextArrows.length; i++ ) {
          this.nextArrows[i].setAttribute('disabled' , 'true');
        }
      }

      // when not on the first slide, enable previous
      if ( nextIndex != 0 ) {
        for ( let i = 0; i < this.previousArrows.length; i++ ) {
          this.previousArrows[i].removeAttribute('disabled');
        }
      }
    }

    this.currentMediaIndex = nextIndex;
    this.updateSlideImage();
    this.updateSelectedColorBasedOnSlideImage();
    this.updateUrlOnButton();

    this.nextClick++
    
    return;
  }

  updateSlideImage() {
    if ( this.productMedia.length > 0 ) {
      const mediaToShow = this.productMedia[ this.currentMediaIndex ];

      const productCard = this.rootElement.closest('.product-card');
      
      if ( productCard != null ) {
        const imageToUpdate = productCard.querySelector('responsive-image.product-card__image img');
        
        if ( imageToUpdate != null ) {
          imageToUpdate.setAttribute( 'srcset' , mediaToShow.image.srcset );
          imageToUpdate.setAttribute( 'src' , mediaToShow.image.src );
          imageToUpdate.setAttribute( 'alt' , mediaToShow.alt );
        }

        // if on mobile
        const mobileImageToUpdate = productCard.querySelector('responsive-image.product-card__image img');
        if ( window.matchMedia('(max-width: 768px)').matches ) {
          mobileImageToUpdate.setAttribute( 'srcset' , mediaToShow.image.srcset );
          mobileImageToUpdate.setAttribute( 'src' , mediaToShow.image.src );
          mobileImageToUpdate.setAttribute( 'alt' , mediaToShow.alt );
        }
      }
    }
  }
  
  updateSelectedColorBasedOnSlideImage() {
    if ( this.productMedia.length > 0 ) {
      const mediaToShow = this.productMedia[ this.currentMediaIndex ];
      const colorOption = mediaToShow.color_info.related_color_handle;
      const currentColorSelected = this.currentSelectedColor;
      const productCard = this.rootElement.closest('.product-card');

      if ( colorOption != currentColorSelected ) {
        this.currentSelectedColor = colorOption;
        if ( productCard != null ) {
          const allButtons = productCard.querySelectorAll( '.swatch-list-item' );
          for ( let i = 0; i < allButtons.length; i++ ) {
            allButtons[i].classList.remove('active-color');
          }
          
          
          const relatedButtons = productCard.querySelectorAll(`[data-color-option="${colorOption}"]`);
          for ( let b = 0; b < relatedButtons.length; b++ ) {
            const relatedSwatch = relatedButtons[b].parentElement;
            const relatedSwatchFile = relatedSwatch.getAttribute('data-base');
            for ( let i = 0; i < allButtons.length; i++ ) {
              if ( allButtons[i].getAttribute('data-base') == relatedSwatchFile ) {
                allButtons[i].classList.add('active-color');
              }
            }
          }
        }
      }

    }
  }

  setSliderIndexByColorClicked( fileName ) {
    let newIndex = 0;
    for ( let i = 0; i < this.productMedia.length; i++ ) {
      const noExtensionFilename = fileName.split('.')[0];
      if ( this.productMedia[i].image.file == noExtensionFilename ) {
        newIndex = i;
        break;
      }
    }

    // update prev / next buttons when not an infinte slider
    if ( !this.infiniteSlider ) {
      if ( newIndex == this.totalMediaSlides - 1 ) {
        for ( let i = 0; i < this.nextArrows.length; i++ ) {
          this.nextArrows[i].setAttribute('disabled' , 'true');
        }
      }
      else if ( newIndex == 0 ) {
        for ( let i = 0; i < this.previousArrows.length; i++ ) {
          this.previousArrows[i].setAttribute('disabled' , 'true');
        }
      }
      else if ( newIndex != 0 && newIndex != this.totalMediaSlides - 1 ) {
        for ( let i = 0; i < this.nextArrows.length; i++ ) {
          this.nextArrows[i].removeAttribute('disabled');
        }

        for ( let i = 0; i < this.previousArrows.length; i++ ) {
          this.previousArrows[i].removeAttribute('disabled');
        }
      }
    }

    this.currentMediaIndex = newIndex;
    this.updateSlideImage();
    this.updateSelectedColorBasedOnSlideImage();
    this.updateUrlOnButton();
  }

  updateUrlOnButton() {
    const productCard = this.rootElement.closest('.product-card');
    if ( productCard == null ) {
      return;
    }

    const relatedButton = productCard.querySelector( `[data-base] [data-color-option="${this.currentSelectedColor}"]`);
    const quickviewButtons = productCard.querySelectorAll('[data-selected-variant-id]');
    const fullWidthLink = productCard.querySelector('a.full-width-link');
    if ( relatedButton != null ) {
      const queryString = relatedButton.getAttribute('data-href');
      const splitString = queryString.split('?variant=');
      const variantId = splitString[ splitString.length - 1 ];

      if ( fullWidthLink != null ) {
        fullWidthLink.href = queryString;
      }

      for ( let i = 0; i < quickviewButtons.length; i++ ) {
        quickviewButtons[i].setAttribute( 'data-selected-variant-id' , variantId );

        if ( quickviewButtons[i].classList.contains('link') ) {
          quickviewButtons[i].href = queryString;
          quickviewButtons[i].removeEventListener( 'click' , emulateLinkPress );
          quickviewButtons[i].addEventListener('click' , emulateLinkPress );
        }
      }
    }
  }
}
customElements.define( "product-card-slider" , ProductCardSlider );

class EventTracker extends HTMLElement {
  constructor() {
    super();

    this.rootElement = this;
    this.relatedToId = this.dataset.relatedToId;
    this.collectionId = this.dataset.collectionId || -1;
    this.collectionHandle = this.dataset.collectionHandle || '';
    this.sectionId = this.dataset.sectionId || '-1',
    this.templateType = this.dataset.templateType || 'unknown';

    this.recordedFirstEvent = false;
    this.eventDetails = null;

    this.watchlistTargets = {
      previousSlideButton: 'product-card-slider .previous',
      previousSlideButtonSvg: 'product-card-slider .previous svg',
      nextSlideButton: 'product-card-slider .next',
      nextSlideButtonSvg: 'product-card-slider .next svg',
      quickAddButton: '.new-quickshop-button',
      swatchListItem: '.swatch-list-item',
      swatchListItemButton: '.swatch-list-item button',
      swatchListItemDiv: '.swatch-list-item div',
    }

    this.rootElement.addEventListener( 'click' , this.logEvent.bind( this ) );
  }

  logEvent( event ) {
    // console.log( 'running event logger...' , this.recordedFirstEvent , event.isTrusted );
    if ( this.recordedFirstEvent ) {
      return;
    }

    if ( event.isTrusted == false ) {
      return;
    }

    const triggerElement = event.target;
    // console.log( triggerElement );
    let isTriggerElementOnTargetList = false;
    const validSelectors = Object.values( this.watchlistTargets );
    let selectorValue = '-1';
    for ( let i = 0; i < validSelectors.length; i++ ) {
      const el = this.rootElement.querySelectorAll( validSelectors[i] );
      for ( let j = 0; j < el.length; j++ ) {
        if ( el[j] == triggerElement ) {
          isTriggerElementOnTargetList = true;
          break;
        }
      }

      if ( isTriggerElementOnTargetList ) {
        selectorValue = validSelectors[i];
        break;
      }
      
    }
    
    if ( isTriggerElementOnTargetList ) {
      const eventDetails = {
        id: this.relatedToId,
        triggerElement: triggerElement,
        querySelectedTarget: selectorValue,
        timestamp: new Date(),
        collectionHandle: this.collectionHandle,
        collectionId: this.collectionId,
        sectionId: this.sectionId,
        templateType: this.templateType
      };
  
      this.eventDetails = eventDetails;
      this.recordedFirstEvent = true;

      this.eventCallback( eventDetails );
    }
    
  }

  eventCallback( event ) {
    // console.log( 'do something in GA4' );
    // console.log( event );

    try {
      if ( window.lillapAnalytics ) {
        window.lillapAnalytics.trackQUICKSlide();
      }
    } catch ( gaerror ) {
      // do nothing
    }

    return;
  }
}
customElements.define( "event-tracker" , EventTracker );

function emulateLinkPress( e ) {
  e.preventDefault();
  const grid = document.querySelector('product-grid-container');
  if ( grid != null ) {
    const idOfEl = e.target.id;
    grid.saveProductButtonIdToFocus( `#${idOfEl}` );
    console.log( grid.currentPage );
    grid.saveCurrentPage( grid.currentPage );
  }

  window.location.href = this.href;
}